/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                          WELCOME SCREEN                                   ║
 * ║              Main Hub + Onboarding Flow Controller                        ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║                                                                           ║
 * ║  This component serves TWO purposes:                                      ║
 * ║  1. Main dashboard hub with navigation buttons                            ║
 * ║  2. Orchestrates the onboarding modal flow for new users                  ║
 * ║                                                                           ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  ONBOARDING FLOW (in order):                                              ║
 * ║                                                                           ║
 * ║  1. WelcomeBackModal         → Greeting message                           ║
 * ║  2. ProfileSetupModal        → Name, phone, avatar                        ║
 * ║  3. FirstMessageModal        → "Anything you've always wanted to tell me?"║
 * ║  4. SubscriptionOnboardingModal → Plan selection + payment                ║
 * ║     └─ OR "I'll do later"    → Sets payment_deferred_at                   ║
 * ║  5. PostPaymentWelcomeModal  → Final welcome (if paid)                    ║
 * ║                                                                           ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  KEY BEHAVIORS:                                                           ║
 * ║                                                                           ║
 * ║  • FirstMessageModal is REQUIRED (cannot be skipped)                      ║
 * ║  • Message sent immediately via POST /api/subscriptions/onboarding/first-message
 * ║                                                                           ║
 * ║  • SubscriptionOnboardingModal can be skipped if user has trial           ║
 * ║  • Skip sets payment_deferred_at via POST /api/subscriptions/defer-payment║
 * ║  • Users who skip show RED in admin inbox after 7 days                    ║
 * ║                                                                           ║
 * ║  • TrialCountdownPill shows days remaining for trialing users             ║
 * ║  • Clicking pill navigates to /subscription page                          ║
 * ║                                                                           ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  LAYOUT (when not in onboarding):                                         ║
 * ║  - Row 1: Text (full width)                                               ║
 * ║  - Row 2: Gallery (full width)                                            ║
 * ║  - Row 3: Account, Wallet, Vault, Newsroom (4 columns)                    ║
 * ║  - Trial pill (if trialing)                                               ║
 * ║                                                                           ║
 * ║  All styling is configurable in config/welcomeConfig.ts                   ║
 * ║                                                                           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useNavigate, useSearchParams } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/features/auth'
import { useAccessLevel, clearAccessLevelCache } from '@/hooks/useAccessLevel'
import { WelcomeBackModal } from '@/components/auth/WelcomeBackModal'
import { ProfileSetupModal } from '@/components/auth/ProfileSetupModal'
import { FirstMessageModal } from '@/components/auth/FirstMessageModal'
import { YearAccessModal } from '@/components/auth/YearAccessModal'
import { PostPaymentWelcomeModal } from '@/components/auth/PostPaymentWelcomeModal'
import {
  welcomeColors,
  welcomeText,
  welcomeLayout,
  welcomeButtons,
  welcomeFeatures
} from '@/config/welcomeConfig'
import { BirthdayBackground } from '@/components/ui/BirthdayBackground'
import {
  TextIcon,
  GalleryIcon,
  AccountIcon,
  VaultIcon,
  WalletIcon,
  StatusIcon,
  NewsroomIcon,
  MailIcon
} from '@/components/icons/OutlineIcons'
import { useVipStatus } from '@/hooks/useVipStatus'
import { EnvelopeModal } from '@/features/mailing/EnvelopeModal'
import { useMailingAddress } from '@/features/mailing/useMailingAddress'

// Icon mapping
const iconMap = {
  text: TextIcon,
  gallery: GalleryIcon,
  account: AccountIcon,
  vault: VaultIcon,
  wallet: WalletIcon,
  status: StatusIcon,
  newsroom: NewsroomIcon,
  mail: MailIcon
}

export function WelcomeScreen() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [showWelcomeModal, setShowWelcomeModal] = useState(false)
  const [showProfileSetupModal, setShowProfileSetupModal] = useState(false)
  const [showFirstMessageModal, setShowFirstMessageModal] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [showPostPaymentWelcome, setShowPostPaymentWelcome] = useState(false)
  // inviteToken is captured during onboarding but no longer forwarded to the payment
  // modal (the $50 year-access flow does not consume an invite token). Kept as a setter
  // so the surrounding invite-detection logic is unchanged.
  const [, setInviteToken] = useState<string | null>(null)
  const [showEnvelopeModal, setShowEnvelopeModal] = useState(false)
  const { hasAddress, isLoading: isMailingLoading, refresh: refreshMailing } = useMailingAddress()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isVipActive } = useVipStatus()

  // Use the access level hook to get trial days remaining
  // This is the single source of truth for subscription/trial status
  const { trialDaysRemaining, expiresAt, state, accessLevel, hasAccess } = useAccessLevel()

  // Get grace days from accessLevel if available
  const graceDaysRemaining = accessLevel?.graceDaysRemaining ?? null

  // Calculate days remaining from multiple sources (trial, grace, or expiresAt)
  const calculateDaysRemaining = (): number | null => {
    // Priority 1: Explicit trial days from backend
    if (trialDaysRemaining !== null && trialDaysRemaining > 0) {
      return trialDaysRemaining
    }

    // Priority 2: Grace period days
    if (graceDaysRemaining !== null && graceDaysRemaining > 0) {
      return graceDaysRemaining
    }

    // Priority 3: Calculate from expiresAt for any expiring access
    if (expiresAt) {
      const expires = new Date(expiresAt)
      const now = new Date()
      const diffMs = expires.getTime() - now.getTime()
      const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
      if (days > 0) {
        return days
      }
    }

    return null
  }

  const daysRemaining = calculateDaysRemaining()

  // Auto-open envelope modal when redirected from /vip with openMailbox param
  useEffect(() => {
    if (searchParams.get('openMailbox') === 'true' && !hasAddress && !isMailingLoading) {
      setShowEnvelopeModal(true)
      searchParams.delete('openMailbox')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, hasAddress, isMailingLoading, setSearchParams])

  // Ref to track if onboarding has started - prevents loop when user object isn't updated
  const onboardingStartedRef = useRef(false)

  // Check if user already has a subscription OR premium access
  const checkIfNeedsSubscription = async () => {
    if (!user?.id) return

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')

      // Check if user already has active subscription - if so, skip subscription modal
      const accessLevelResponse = await fetch(`${API_URL}/api/subscriptions/access/level`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (accessLevelResponse.ok) {
        const accessLevel = await accessLevelResponse.json()
        // Only skip if user has active Stripe subscription
        if (accessLevel.hasAccess && (accessLevel.state === 'ACTIVE' || accessLevel.state === 'TRIALING')) {
          return
        }
      }

      // CRITICAL: Check if user has pending invite FIRST before checking subscription
      // This includes customer_benefits users who get auto-generated invites
      const inviteResponse = await fetch(`${API_URL}/api/subscriptions/invites/check-by-email?email=${encodeURIComponent(user.email)}`)
      if (inviteResponse.ok) {
        const inviteData = await inviteResponse.json()
        if (inviteData.hasInvite && inviteData.invite?.token) {
          // Store invite token for subscription modal
          localStorage.setItem('pending_invite_token', inviteData.invite.token)
          setInviteToken(inviteData.invite.token)

          // If needsWelcome flag was set, they need to complete onboarding FIRST
          // Don't show subscription modal yet - handleProfileSetupComplete will handle it
          const needsWelcome = localStorage.getItem('needsWelcome')
          if (needsWelcome === 'true') {
            return
          }

          // Otherwise, show subscription modal with their invite
          setShowSubscriptionModal(true)
          return
        }
      }

      // Check both subscription AND access status
      const [subResponse, accessResponse] = await Promise.all([
        fetch(`${API_URL}/api/subscriptions/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/subscriptions/access`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ])

      const subData = subResponse.ok ? await subResponse.json() : null
      const accessData = accessResponse.ok ? await accessResponse.json() : null

      // Note: Trial days remaining is now handled by useAccessLevel hook
      // No need to manually calculate here

      // Show subscription modal if:
      // 1. No subscription at all, OR
      // 2. Subscription exists but is in a state that requires re-subscription
      const needsSubscription = !subData?.subscription ||
        ['incomplete', 'incomplete_expired', 'canceled', 'unpaid'].includes(subData?.subscription?.status)

      if (needsSubscription && !accessData?.hasAccess) {
        setShowSubscriptionModal(true)
      } else {
        // Clear any pending flags
        localStorage.removeItem('pending_invite_token')
        localStorage.removeItem('has_pending_subscription')
      }
    } catch (error) {
      console.error('[WelcomeScreen] Error checking subscription:', error)
    }
  }

  // Check if user needs welcome flow on mount
  useEffect(() => {
    const needsWelcome = localStorage.getItem('needsWelcome')
    const pendingInvite = localStorage.getItem('pending_invite_token')
    if (pendingInvite) {
      setInviteToken(pendingInvite)
    }

    // IMPORTANT: Show onboarding FIRST if needed
    // Check BOTH localStorage flag AND actual user profile data
    // BUT only if we haven't already started onboarding in this session (prevents loop)
    // AND only if onboarding hasn't been completed (prevents post-payment loop)
    // AND only if user doesn't already have access (active sub = already onboarded)
    const onboardingComplete = localStorage.getItem('onboarding_complete') === 'true'
    const userNeedsOnboarding = !onboardingStartedRef.current && !onboardingComplete && !hasAccess && (
      needsWelcome === 'true' ||
      (user && (!user.firstName && !(user as any).first_name))
    )

    if (userNeedsOnboarding) {
      onboardingStartedRef.current = true // Mark that onboarding has started - prevents re-triggering
      setShowWelcomeModal(true)
      // Clear the flag so it doesn't show again on refresh
      localStorage.removeItem('needsWelcome')
      // DON'T check subscription - onboarding will handle showing subscription modal after completion
      return // IMPORTANT: Don't run any other checks
    }

    // If onboarding modals are open, don't check subscription
    if (showWelcomeModal || showProfileSetupModal || showFirstMessageModal) {
      return
    }

    // If user has pending invite, check if they're a returning user or new user
    // This handles both: manually invited users AND customer_benefits users who get auto-invite
    if (pendingInvite && user?.id) {
      const hasCompletedProfile = user.firstName || (user as any).first_name

      if (!hasCompletedProfile) {
        // New user who hasn't set up profile yet - wait for profile setup
        // Don't call checkIfNeedsSubscription() yet - handleProfileSetupComplete will handle it
        return
      } else {
        // Returning user with stale invite token - clear it and check subscription
        // This fixes the bug where users get stuck in redirect loop when their subscription expires
        localStorage.removeItem('pending_invite_token')
        // Call subscription check directly since local pendingInvite var still has old value
        checkIfNeedsSubscription()
        return
      }
    }

    // ONLY check subscription if:
    // 1. User is loaded
    // 2. No onboarding in progress (neither modal is open)
    // 3. No pending invite
    // 4. needsWelcome flag is not set
    if (user?.id && !needsWelcome && !pendingInvite && !showWelcomeModal && !showProfileSetupModal && !showFirstMessageModal) {
      // User is logged in AND already completed onboarding - check if they need subscription
      checkIfNeedsSubscription()
    } else {
      // no-op
    }
  }, [user, showWelcomeModal, showProfileSetupModal, showFirstMessageModal])

  // Handle welcome modal continue
  const handleWelcomeContinue = () => {
    setShowWelcomeModal(false)
    setShowProfileSetupModal(true)
  }

  // Handle profile setup complete
  const handleProfileSetupComplete = async () => {
    setShowProfileSetupModal(false)

    // Check if user already has a conversation/message - if so, skip FirstMessageModal
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')

      const response = await fetch(`${API_URL}/api/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        const hasExistingConversation = data.conversations && data.conversations.length > 0

        if (hasExistingConversation) {
          // Go directly to subscription modal
          showSubscriptionModalAfterOnboarding()
          return
        }
      }
    } catch (err) {
      console.warn('[WelcomeScreen] Could not check for existing conversations:', err)
      // Continue with first message modal on error
    }

    // Show FirstMessageModal BEFORE subscription modal
    // This is part of the onboarding flow: Profile -> First Message -> Subscription
    setShowFirstMessageModal(true)
  }

  // Helper function to show subscription modal after onboarding
  // Checks if user already has an active subscription first to avoid duplicate prompts
  const showSubscriptionModalAfterOnboarding = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')

      // Check if user already has active subscription - skip modal if so
      const accessLevelResponse = await fetch(`${API_URL}/api/subscriptions/access/level`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (accessLevelResponse.ok) {
        const accessData = await accessLevelResponse.json()
        if (accessData.hasAccess && (accessData.state === 'ACTIVE' || accessData.state === 'TRIALING')) {
          // Already subscribed - skip subscription modal, go straight to post-payment welcome
          localStorage.removeItem('pending_invite_token')
          setShowPostPaymentWelcome(true)
          return
        }
      }
    } catch (err) {
      console.warn('[WelcomeScreen] Could not check subscription status:', err)
      // Continue to show modal on error - better to show it than skip it
    }

    const pendingInvite = localStorage.getItem('pending_invite_token')
    if (pendingInvite) {
      setInviteToken(pendingInvite)
    }
    setShowSubscriptionModal(true)
  }

  // Handle first message submission
  const handleFirstMessageSubmit = async (message: string) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')

      // Save first message to backend
      const response = await fetch(`${API_URL}/api/subscriptions/onboarding/first-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message })
      })

      if (response.ok) {
        // no-op
      } else {
        console.warn('[WelcomeScreen] Failed to save first message:', await response.text())
      }
    } catch (err) {
      console.error('[WelcomeScreen] Failed to save first message:', err)
      // Continue anyway - don't block onboarding
    }

    setShowFirstMessageModal(false)

    // ALWAYS show subscription modal after first message (onboarding requirement)
    // Users with invite get their trial, users without must pay to enter
    showSubscriptionModalAfterOnboarding()
  }

  // Handle subscription setup complete
  const handleSubscriptionComplete = () => {
    setShowSubscriptionModal(false)
    setInviteToken(null)
    // Mark onboarding as fully complete so we never loop back
    localStorage.setItem('onboarding_complete', 'true')
    localStorage.removeItem('needsWelcome')
    // Show post-payment welcome modal
    setShowPostPaymentWelcome(true)
  }


  // Handle post-payment welcome complete
  const handlePostPaymentWelcomeComplete = () => {
    setShowPostPaymentWelcome(false)
    // Clear cached access level so ProtectedRoute sees the new subscription
    clearAccessLevelCache()
    // Force full page reload to refetch everything fresh
    window.location.href = '/messaging'
  }

  // Render a single button
  const renderButton = (button: any, index: number, buttonType: 'row1-2' | 'row3' = 'row3') => {
    const IconComponent = iconMap[button.icon as keyof typeof iconMap]

    // For row 1 & 2: text centered, icon on right
    // For row 3: icon only, no text
    if (buttonType === 'row1-2') {
      return (
        <button
          key={button.id}
          onClick={() => navigate(button.route)}
          className={`
            ${welcomeColors.buttonBackground}
            ${welcomeColors.buttonHover}
            ${welcomeColors.buttonRounded}
            ${welcomeColors.buttonText}
            ${welcomeLayout.buttonMinHeight}
            p-4
            flex items-center justify-between
            btn-hover
            ${welcomeFeatures.enableAnimations ? welcomeLayout.fadeInAnimation : ''}
            font-bold
          `}
          style={
            welcomeFeatures.enableAnimations
              ? { animationDelay: `${0.2 + index * 0.05}s`, animationFillMode: 'backwards' }
              : undefined
          }
        >
          <span className="flex-1 text-center text-sm">{button.label}</span>
          <IconComponent
            size={welcomeLayout.iconSize}
            className={`${welcomeColors.iconColor}`}
          />
        </button>
      )
    }

    // Row 3: Icon only, no text
    return (
      <button
        key={button.id}
        onClick={() => navigate(button.route)}
        className={`
          ${welcomeColors.buttonBackground}
          ${welcomeColors.buttonHover}
          ${welcomeColors.buttonRounded}
          ${welcomeColors.buttonText}
          ${welcomeLayout.buttonMinHeight}
          p-4
          flex items-center justify-center
          btn-hover
          ${welcomeFeatures.enableAnimations ? welcomeLayout.fadeInAnimation : ''}
          font-bold
        `}
        style={
          welcomeFeatures.enableAnimations
            ? { animationDelay: `${0.2 + index * 0.05}s`, animationFillMode: 'backwards' }
            : undefined
        }
      >
        <IconComponent
          size={welcomeLayout.iconSize}
          className={`${welcomeColors.iconColor}`}
        />
      </button>
    )
  }

  return (
    <>
      {/* Welcome Flow Modals */}
      <WelcomeBackModal
        isOpen={showWelcomeModal}
        onContinue={handleWelcomeContinue}
      />

      {user && (
        <ProfileSetupModal
          isOpen={showProfileSetupModal}
          user={user}
          onComplete={handleProfileSetupComplete}
        />
      )}

      {/* First Message Modal - Shows after profile setup, before subscription */}
      <FirstMessageModal
        isOpen={showFirstMessageModal}
        onSubmit={handleFirstMessageSubmit}
      />

      {user && (
        <YearAccessModal
          isOpen={showSubscriptionModal}
          onComplete={handleSubscriptionComplete}
        />
      )}

      {/* Post-Payment Welcome Modal */}
      <PostPaymentWelcomeModal
        isOpen={showPostPaymentWelcome}
        onContinue={handlePostPaymentWelcomeComplete}
      />

      {/* Envelope Modal for mailing address collection */}
      <EnvelopeModal
        isOpen={showEnvelopeModal}
        onClose={() => setShowEnvelopeModal(false)}
        onAddressSaved={refreshMailing}
      />

      {/* Mailbox rendered inside the buttons container below */}

      {/* Main Welcome Screen */}
      <div className={`relative min-h-screen ${welcomeColors.background}`}>
        <BirthdayBackground className="min-h-screen" enabled={false}>
          <div className={`flex items-start justify-center min-h-screen ${welcomeLayout.padding} pt-12`}>
            <div className={`w-full ${welcomeLayout.maxWidth}`}>
        {/* Header */}
        <div className={`text-center ${welcomeLayout.headerMarginBottom} ${welcomeFeatures.enableAnimations ? welcomeLayout.animationDuration : ''}`}>
          {welcomeText.title && (
            <h1 className={`text-xl font-bold ${welcomeColors.titleText} mb-2`}>
              {welcomeText.title}
            </h1>
          )}
          <h2 className={`text-lg font-bold ${welcomeColors.subtitleText}`}>
            {welcomeText.subtitle}
          </h2>
        </div>

        {/* Buttons Container wrapper - mailbox sits behind the white box */}
        <div className="relative">
          {/* Mailbox removed */}

          <div className={`
            relative z-10
            ${welcomeColors.containerBackground}
            ${welcomeColors.containerRounded}
            ${welcomeColors.containerShadow}
            ${welcomeLayout.containerPadding}
            ${welcomeFeatures.enableAnimations ? `${welcomeLayout.animationDuration} ${welcomeLayout.animationDelay}` : ''}
          `}>
          {/* Click me label removed */}

          {/* Row 1: Text button (full width) - text centered, icon right */}
          <div className={`grid grid-cols-1 ${welcomeLayout.buttonGap}`}>
            {welcomeButtons.row1.map((button, index) => renderButton(button, index, 'row1-2'))}
          </div>

          {/* Row 2: Gallery button (full width) - text centered, icon right */}
          <div className={`grid grid-cols-1 ${welcomeLayout.buttonGap} mt-4`}>
            {welcomeButtons.row2.map((button, index) => renderButton(button, index + 1, 'row1-2'))}
          </div>

          {/* Row 3: Account, Vault (2 columns) - icon only */}
          <div className={`grid grid-cols-2 ${welcomeLayout.buttonGap} mt-4`}>
            {welcomeButtons.row3.map((button, index) => renderButton(button, index + 2, 'row3'))}
          </div>

          {/* Row 4: Mail icon (only for VIP mailing subscribers) */}
          {isVipActive && (
            <div className={`grid grid-cols-1 ${welcomeLayout.buttonGap} mt-4`}>
              <button
                onClick={() => navigate('/mail')}
                className={`
                  ${welcomeColors.buttonBackground} ${welcomeColors.buttonHover}
                  ${welcomeColors.buttonRounded} ${welcomeColors.buttonText}
                  ${welcomeLayout.buttonMinHeight}
                  p-4 flex items-center justify-center btn-hover
                `}
              >
                <MailIcon size={welcomeLayout.iconSize} className={welcomeColors.iconColor} />
              </button>
            </div>
          )}

          {/* Show pill for ANY user with days remaining (trial, deferred, canceled, grace) */}
          {/* {daysRemaining !== null && daysRemaining > 0 && (
            <div className="mt-4 flex justify-center">
              <TrialCountdownPill
                daysRemaining={daysRemaining}
                expiresAt={expiresAt || undefined}
                size="md"
                showIcon={true}
              />
            </div>
          )} */}
          {/* Show expired message if no days remaining and not active subscriber */}
          {(daysRemaining === null || daysRemaining <= 0) && state !== 'ACTIVE' && expiresAt && new Date(expiresAt) < new Date() && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={() => navigate('/subscription')}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium border border-red-200 hover:bg-red-200 transition-colors"
              >
                Access ended - Subscribe now
              </button>
            </div>
          )}
        </div>
        </div>{/* end wrapper for mailbox + white box */}

        {/* User Info (small text at bottom) */}
        {welcomeFeatures.showUserInfo && user && (
          <p className={`text-center mt-4 text-xs ${welcomeColors.footerText} ${welcomeFeatures.enableAnimations ? `${welcomeLayout.fadeInAnimation} animate-delay-300` : ''}`}>
            {welcomeText.footerPrefix} {user.email}
          </p>
        )}

        {/* Moon decorative element */}
        <div className="flex justify-center mt-4">
          <svg viewBox="0 0 100 100" className="w-10 h-10 text-[#87CEEB] opacity-40">
            <path d="M50 5 A45 45 0 1 0 50 95 A35 35 0 1 1 50 5" fill="currentColor"/>
          </svg>
        </div>
            </div>
          </div>
        </BirthdayBackground>
      </div>
    </>
  )
}
